import React, { useEffect, useRef, useMemo } from 'react'
import { Box, Center, Spinner, useDisclosure } from '@chakra-ui/react'
import CommentsPanel from '../../components/features/Comments/CommentsPanel/CommentsPanel'
import SearchPanel from '../../components/features/Search/SearchPanel'
import { observer } from 'mobx-react'
import rootStore from '../../store/RootStore'
import Layout from '../../components/ui/Layout/Layout'
import Navbar from '../../components/features/Navbar/Navbar'
import { LeftPanel } from '../../components/ui/Layout/LeftPanel'
import { RightPanel } from '../../components/ui/Layout/RightPanel'
import { ReportMenu } from './ReportMenu/ReportMenu'
import { DownloadFooter } from './DownloadFooter/DownloadFooter'
import SectionList from './SectionList'
import { ReportContext } from '../../contexts/ReportContext'
import ReactGA from 'react-ga4'
import { useParams, useSearchParams } from 'react-router-dom'

const ReportDetailPage = observer(() => {
  const {
    reportStore: { currentReport, loadingReport },
    searchStore,
    commentStore,
  } = rootStore

  const { searchDrawerVisible, setSearchDrawerVisible } = searchStore
  const { commentsDrawerVisible, setCommentsDrawerVisible } = commentStore

  const { reportId } = useParams()
  const [searchParams] = useSearchParams()
  const snapshotId = searchParams.get('snapshot') || undefined
  const refScrollContainer = useRef(null)

  // Google Analytics tracking
  useEffect(() => {
    if (currentReport?.name) {
      ReactGA.send({
        hitType: 'pageview',
        page: `/reports/${reportId}`,
        title: currentReport.name,
      })
    }
  }, [currentReport?.name, reportId])

  // Handle download panel state
  const downloadControls = useDisclosure()
  const { isOpen, onToggle, onClose } = downloadControls

  // Context value memoization to prevent unnecessary re-renders
  const reportContextValue = useMemo(
    () => ({
      isDownloadOpen: isOpen,
      onDownloadToggle: onToggle,
      onDownloadClose: onClose,
      refScrollContainer,
    }),
    [isOpen, onToggle, onClose]
  )

  // Effect to handle panel visibility conflicts
  useEffect(() => {
    if (commentsDrawerVisible) {
      setSearchDrawerVisible(false)
    }
  }, [commentsDrawerVisible, setSearchDrawerVisible])

  useEffect(() => {
    if (searchDrawerVisible) {
      setCommentsDrawerVisible(false)
    }
  }, [searchDrawerVisible, setCommentsDrawerVisible])

  return (
    <Layout>
      <ReportContext.Provider value={reportContextValue}>
        <LeftPanel keepOpen>
          <ReportMenu />
        </LeftPanel>

        <Box
          position="relative"
          background="brand.lightestGray"
          height="100vh">
          <Navbar />
          <Box
            overflowY="auto"
            height="calc(100vh - var(--navbar-height, 180px))"
            top="var(--navbar-height, 180px)"
            position="absolute"
            left="0"
            right="0"
            padding={{ base: '24px', md: '0 32px' }}
            ref={refScrollContainer}>
            <SectionList reportId={reportId} snapshotId={snapshotId} />
          </Box>
        </Box>

        <DownloadFooter />

        <RightPanel>
          {searchDrawerVisible && <SearchPanel />}
          {commentsDrawerVisible && <CommentsPanel />}
        </RightPanel>
      </ReportContext.Provider>
    </Layout>
  )
})

ReportDetailPage.displayName = 'ReportDetailPage'

export default ReportDetailPage
